import { useCallback } from 'react';
import noop from 'lodash/noop';
import { Dropdown, type Option } from '@/UI/Dropdown/Dropdown';
import { TextSmRg } from '@/UI/AppTypography/AppTypography';
import { CaretIcon } from '@/UI/Icons';
import { Flex } from '@/UI/styles';
import { color } from '@/colors';
import { dropdownTriggerSx, rowGrid } from './missingWorkflowFontsModal.styles';
import type { CSSProperties, ReactNode } from 'react';
import type { BuiltInFont } from 'web';

export type MissingFontFamilyAccordionHeaderProps = {
  fontFamilyKey: string;
  isExpanded: boolean;
  familyRegionId: string;
  familyTriggerId: string;
  onToggle: () => void;
  commonHeaderFont: BuiltInFont | null;
  /** True when faces in this family use more than one replacement font; header shows "Mixed" until a single choice applies to all. */
  isReplacementMixed: boolean;
  fontOptions: Option<BuiltInFont>[];
  emptyFontPlaceholder: ReactNode;
  mixedFontPlaceholder: ReactNode;
  noOptionsText: string;
  onHeaderFontChange: (font: BuiltInFont) => void;
};

function caretIconStyle(isExpanded: boolean): CSSProperties {
  return {
    flexShrink: 0,
    color: color.White64_T,
    transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
    transition: 'transform 150ms ease-out',
  };
}

export function MissingFontFamilyAccordionHeader({
  fontFamilyKey,
  isExpanded,
  familyRegionId,
  familyTriggerId,
  onToggle,
  commonHeaderFont,
  isReplacementMixed,
  fontOptions,
  emptyFontPlaceholder,
  mixedFontPlaceholder,
  noOptionsText,
  onHeaderFontChange,
}: MissingFontFamilyAccordionHeaderProps) {
  const handleFontDropdownChange = useCallback(
    (opt: Option<BuiltInFont>) => {
      onHeaderFontChange(opt.value);
    },
    [onHeaderFontChange],
  );

  return (
    <Flex
      sx={{
        width: '100%',
        py: 0.5,
        minHeight: 40,
        boxSizing: 'border-box',
        alignItems: 'center',
        ...(isExpanded
          ? { display: 'flex' }
          : {
              ...rowGrid,
            }),
      }}
    >
      <button
        type="button"
        aria-expanded={isExpanded}
        aria-controls={familyRegionId}
        id={familyTriggerId}
        onClick={onToggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          minWidth: 0,
          width: '100%',
          padding: 0,
          textAlign: 'left',
          borderRadius: 0,
          border: 'none',
          background: 'none',
          font: 'inherit',
          color: 'inherit',
          cursor: 'pointer',
          justifyContent: 'flex-start',
        }}
      >
        <CaretIcon width={12} height={12} style={caretIconStyle(isExpanded)} aria-hidden />
        <TextSmRg sx={{ color: color.White100, overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
          {fontFamilyKey}
        </TextSmRg>
      </button>
      {!isExpanded ? (
        <>
          <TextSmRg
            component="span"
            sx={{
              color: color.White16_T,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 16,
              userSelect: 'none',
            }}
          >
            →
          </TextSmRg>
          <Flex sx={{ minWidth: 0, width: '100%' }}>
            <Dropdown
              value={isReplacementMixed ? null : commonHeaderFont}
              confirmedValue={isReplacementMixed ? null : commonHeaderFont}
              options={fontOptions}
              onChange={handleFontDropdownChange}
              onClose={noop}
              onOpen={noop}
              search
              matchTriggerWidth
              width="100%"
              size="small"
              sx={dropdownTriggerSx}
              emptyState={isReplacementMixed ? mixedFontPlaceholder : emptyFontPlaceholder}
              noOptionsText={noOptionsText}
            />
          </Flex>
        </>
      ) : null}
    </Flex>
  );
}
